import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Area,
  AreaChart,
  BarChart,
  Bar,
  Cell,
  LabelList,
} from "recharts";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://app.nvplaydigital.com");

function dataHojeBR() {
  return new Date().toLocaleDateString("sv-SE", {
    timeZone: "America/Sao_Paulo",
  });
}

export default function Dashboard() {
  const [me, setMe] = useState(null);
  const [resumoHoje, setResumoHoje] = useState(null);
  const [resumoMesMeu, setResumoMesMeu] = useState(null);
  const [resumoHojeRede, setResumoHojeRede] = useState(null);
  const [resumoMesRede, setResumoMesRede] = useState(null);
  const [topRanking, setTopRanking] = useState([]);
  const [rankingColaboradores, setRankingColaboradores] = useState([]);
  const [graficoMeu, setGraficoMeu] = useState([]);
  const [graficoRede, setGraficoRede] = useState([]);
  const [royaltiesConfig, setRoyaltiesConfig] = useState({
    ativo: false,
    percentual: 30,
    tipo: "liquido",
  });
  const [periodoTorre, setPeriodoTorre] = useState("mensal");
  const [mesBaseTorre, setMesBaseTorre] = useState(dataHojeBR().slice(0, 7));

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  useEffect(() => {
    carregarTudo();
  }, []);
useEffect(() => {
  if (!me?.email) return;

  carregarRanking(me);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [periodoTorre, mesBaseTorre, me?.email]);

useEffect(() => {
  if (!me?.email) return;

  carregarRelatorios(me);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [mesBaseTorre, me?.email]);
  async function carregarTudo() {
    const usuario = await carregarMe();

    if (usuario?.email) {
      await carregarConfiguracoesRoyalties();
      await carregarRanking(usuario);
      await carregarRankingColaboradores(usuario);
      await carregarRelatorios(usuario);
    }
  }

  async function carregarMe() {
    const res = await fetch(`${API_BASE}/me`, {
      headers: { Authorization: token },
    });

    const data = await res.json();
    setMe(data);
    return data;
  }

  async function carregarConfiguracoesRoyalties() {
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/configuracoes`, {
        headers: { Authorization: token },
      });

      const dados = await res.json();
      const royalties = dados?.royalties || {};

      setRoyaltiesConfig({
        ativo: Boolean(royalties.ativo),
        percentual: Number(royalties.percentual || 30),
        tipo: royalties.tipo === "bruto" ? "bruto" : "liquido",
      });
    } catch (err) {
      console.log("Erro ao carregar royalties do dashboard:", err);
    }
  }

  async function carregarRanking(usuario) {
    const paramsRanking = new URLSearchParams({
      periodo: periodoTorre || "mensal",
      mes: mesBaseTorre || dataHojeBR().slice(0, 7),
    });

    const queryPeriodo = paramsRanking.toString();
    let rota = `${API_BASE}/ranking?${queryPeriodo}`;

    if (usuario?.role === "franquia" || usuario?.role === "colaborador") {
      rota = `${API_BASE}/ranking-colaboradores?${queryPeriodo}`;
    }

    const res = await fetch(rota, {
      headers: { Authorization: token },
    });

    const data = await res.json();

    if (Array.isArray(data)) {
      setTopRanking(data);
    } else {
      setTopRanking([]);
    }
  }

  async function carregarRankingColaboradores(usuario) {
    if (
      usuario?.role !== "franquia" &&
      usuario?.role !== "gestor" &&
      usuario?.role !== "master"
    ) {
      setRankingColaboradores([]);
      return;
    }

    const res = await fetch(`${API_BASE}/ranking-colaboradores`, {
      headers: { Authorization: token },
    });

    const data = await res.json();

    if (Array.isArray(data)) {
      setRankingColaboradores(data);
    } else {
      setRankingColaboradores([]);
    }
  }

  async function carregarRelatorios(usuarioLogado) {
    const hoje = dataHojeBR();
    const mesAtual = hoje.slice(0, 7);
    let relatoriosFallbackData = null;

    async function carregarMeusRelatoriosParaDashboard() {
      if (Array.isArray(relatoriosFallbackData)) return relatoriosFallbackData;

      try {
        const res = await fetch(`${API_BASE}/meus-relatorios`, {
          headers: { Authorization: token },
        });

        const data = await res.json();
        relatoriosFallbackData = Array.isArray(data) ? data : [];
        return relatoriosFallbackData;
      } catch (err) {
        console.log("Erro ao carregar /meus-relatorios no Dashboard:", err);
        relatoriosFallbackData = [];
        return relatoriosFallbackData;
      }
    }

    function itemEhSnapshotOperacionalDashboard(item) {
      const colaboradoresIncluidos = Array.isArray(item?.colaboradoresIncluidos)
        ? item.colaboradoresIncluidos.length
        : 0;

      return (
        item?.incluiColaboradores === true ||
        item?.fechamentoOperacional === true ||
        (item?.fechado === true && colaboradoresIncluidos > 0)
      );
    }

    function somarColaboradoresSnapshotDashboard(snapshot) {
      const colaboradores = Array.isArray(snapshot?.colaboradoresIncluidos)
        ? snapshot.colaboradoresIncluidos
        : [];

      return colaboradores.reduce(
        (acc, colaborador) => {
          const vendasNovas = Number(colaborador.vendasNovas || 0);
          const renovacoes = Number(colaborador.renovacoes || 0);

          acc.faturamento += Number(colaborador.faturamento || 0);
          acc.vendasNovas += vendasNovas;
          acc.renovacoes += renovacoes;
          acc.leads += Number(colaborador.leads || 0);
          acc.vendas += Number(colaborador.totalVendas || 0) || vendasNovas + renovacoes;

          return acc;
        },
        {
          faturamento: 0,
          vendasNovas: 0,
          renovacoes: 0,
          leads: 0,
          vendas: 0,
        }
      );
    }

    function resumoCorrigidoPorSnapshotDashboard(snapshot) {
      if (!snapshot) return null;

      const colaboradores = somarColaboradoresSnapshotDashboard(snapshot);
      const base = snapshot.baseIndividualFechamento || {};

      let faturamentoBase = Number(base.faturamento || 0);
      let vendasNovasBase = Number(base.vendasNovas || 0);
      let renovacoesBase = Number(base.renovacoes || 0);
      let leadsBase = Number(base.leads || 0);

      const baseVazia =
        faturamentoBase <= 0 &&
        vendasNovasBase <= 0 &&
        renovacoesBase <= 0 &&
        leadsBase <= 0;

      const baseContaminada =
        Math.abs(faturamentoBase - Number(snapshot.faturamento || 0)) <= 0.01 &&
        vendasNovasBase === Number(snapshot.vendasNovas || 0) &&
        renovacoesBase === Number(snapshot.renovacoes || 0);

      if (baseVazia || baseContaminada) {
        faturamentoBase = Math.max(
          0,
          Number(snapshot.faturamento || 0) - Number(colaboradores.faturamento || 0)
        );
        vendasNovasBase = Math.max(
          0,
          Number(snapshot.vendasNovas || 0) - Number(colaboradores.vendasNovas || 0)
        );
        renovacoesBase = Math.max(
          0,
          Number(snapshot.renovacoes || 0) - Number(colaboradores.renovacoes || 0)
        );
        leadsBase = Math.max(
          0,
          Number(snapshot.leads || 0) - Number(colaboradores.leads || 0)
        );
      }

      const gastoTrafego = Number(base.gastoTrafego || snapshot.gastoTrafego || 0);
      const creditoServidor = Number(base.creditoServidor || snapshot.creditoServidor || 0);
      const outrosGastos = Array.isArray(base.outrosGastos)
        ? base.outrosGastos
        : Array.isArray(snapshot.outrosGastos)
        ? snapshot.outrosGastos
        : [];

      const totalOutrosGastos = outrosGastos.reduce(
        (acc, gasto) => acc + Number(gasto.valor || 0),
        0
      );
      const gastos = gastoTrafego + totalOutrosGastos;

      const faturamento = faturamentoBase + Number(colaboradores.faturamento || 0);
      const vendasNovas = vendasNovasBase + Number(colaboradores.vendasNovas || 0);
      const renovacoes = renovacoesBase + Number(colaboradores.renovacoes || 0);
      const leads = leadsBase + Number(colaboradores.leads || 0);
      const vendas = vendasNovas + renovacoes;
      const lucro = faturamento - gastos;

      return {
        faturamento,
        gastos,
        lucro,
        creditoServidor,
        lucroRealEmpresa: lucro - creditoServidor,
        vendas,
        vendasNovas,
        renovacoes,
        leads,
      };
    }

    async function buscarResumoHojeCorrigidoSeRejeitado() {
      const data = await carregarMeusRelatoriosParaDashboard();

      const snapshots = data
        .filter((item) => {
          const status = String(item?.statusAprovacao || "");

          return (
            item?.usuario === usuarioLogado?.email &&
            String(item?.data || "").slice(0, 10) === hoje &&
            itemEhSnapshotOperacionalDashboard(item) &&
            (status === "rejeitado" || status === "aguardando_correcao")
          );
        })
        .sort((a, b) => {
          const dataA = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const dataB = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return dataB - dataA;
        });

      if (snapshots.length === 0) return null;

      return resumoCorrigidoPorSnapshotDashboard(snapshots[0]);
    }

    function aplicarResumoDaMinhaRede(payload) {
      if (!payload || typeof payload !== "object") return false;

      const meuHoje =
        payload.resumoMeuGrupo?.hoje ||
        payload.resumoIndividual?.hoje ||
        payload.resumoSomenteUsuario?.hoje ||
        null;

      const meuMes =
        payload.resumoMeuGrupo?.mes ||
        payload.resumoIndividual?.mes ||
        payload.resumoSomenteUsuario?.mes ||
        null;

      const redeHoje = payload.resumoRede?.hoje || null;
      const redeMes = payload.resumoRede?.mes || null;

      setResumoHoje(meuHoje || somarRelatorios([]));
      setResumoMesMeu(meuMes || somarRelatorios([]));
      setResumoHojeRede(redeHoje || somarRelatorios([]));
      setResumoMesRede(redeMes || somarRelatorios([]));
      setGraficoMeu(payload.evolucaoMeuGrupo || payload.evolucaoIndividual || []);
      setGraficoRede(payload.evolucaoRede || []);

      return true;
    }

    function ehMeuDashboard(r) {
      return r.usuario === usuarioLogado.email;
    }

    function ehColaboradorDiretoDashboard(r) {
      return (
        r.role === "colaborador" &&
        String(r.parentId) === String(usuarioLogado.id)
      );
    }

    function calcularResumoOperacionalMeuDashboard(data, filtro) {
      const lista = Array.isArray(data) ? data : [];

      return somarRelatorios(
        lista.filter((r) => {
          const dataRelatorio = String(r?.data || "").slice(0, 10);

          const dentroDoFiltro =
            filtro.tipo === "dia"
              ? dataRelatorio === filtro.valor
              : dataRelatorio.startsWith(filtro.valor);

          if (!dentroDoFiltro) return false;

          if (itemEhSnapshotOperacionalDashboard(r)) return false;

          return ehMeuDashboard(r) || ehColaboradorDiretoDashboard(r);
        })
      );
    }

    function calcularGraficoOperacionalMeuDashboard(data) {
      const lista = Array.isArray(data) ? data : [];

      return agruparPorMes(
        lista.filter((r) => {
          if (itemEhSnapshotOperacionalDashboard(r)) return false;

          return ehMeuDashboard(r) || ehColaboradorDiretoDashboard(r);
        })
      );
    }

    /*
      CORREÇÃO DO DASHBOARD TRAVADO:
      /minha-rede/:id continua alimentando a REDE, mas "Meu faturamento"
      agora vem sempre da base viva de /meus-relatorios, a mesma usada em Lançamentos.
      Assim, quando você edita o lançamento depois de rejeitar, o Dashboard acompanha.
    */
    if (usuarioLogado?.id && usuarioLogado?.role !== "colaborador") {
      try {
        const dataOperacional = await carregarMeusRelatoriosParaDashboard();

        const resumoHojeOperacional = calcularResumoOperacionalMeuDashboard(
          dataOperacional,
          { tipo: "dia", valor: hoje }
        );

        const resumoMesOperacional = calcularResumoOperacionalMeuDashboard(
          dataOperacional,
          { tipo: "mes", valor: mesAtual }
        );

        const graficoMeuOperacional = calcularGraficoOperacionalMeuDashboard(dataOperacional);

        const queryRedeDashboard = new URLSearchParams({
          periodo: "mensal",
          mes: mesBaseTorre || mesAtual,
        }).toString();

        const resRede = await fetch(`${API_BASE}/minha-rede/${usuarioLogado.id}?${queryRedeDashboard}`, {
          headers: { Authorization: token },
        });

        const dadosRede = await resRede.json();

        if (resRede.ok) {
          dadosRede.resumoMeuGrupo = {
            ...(dadosRede.resumoMeuGrupo || {}),
            hoje: resumoHojeOperacional,
            mes: resumoMesOperacional,
            periodo: resumoMesOperacional,
          };

          dadosRede.resumoIndividual = {
            ...(dadosRede.resumoIndividual || {}),
            hoje: resumoHojeOperacional,
            mes: resumoMesOperacional,
            periodo: resumoMesOperacional,
          };

          dadosRede.resumoSomenteUsuario = {
            ...(dadosRede.resumoSomenteUsuario || {}),
            hoje: resumoHojeOperacional,
            mes: resumoMesOperacional,
            periodo: resumoMesOperacional,
          };

          dadosRede.evolucaoMeuGrupo = graficoMeuOperacional;
          dadosRede.evolucaoIndividual = graficoMeuOperacional;

          if (aplicarResumoDaMinhaRede(dadosRede)) {
            return;
          }
        }
      } catch (err) {
        console.log("Fallback dashboard /meus-relatorios:", err);
      }
    }

    // Fallback antigo preservado para colaborador ou caso /minha-rede/:id falhe.
    const data = await carregarMeusRelatoriosParaDashboard();
    if (!Array.isArray(data)) return;

    function ehMeu(r) {
      return r.usuario === usuarioLogado.email;
    }

    function ehColaboradorDireto(r) {
      return (
        r.role === "colaborador" &&
        String(r.parentId) === String(usuarioLogado.id)
      );
    }

    function relatorioEhConsolidado(r) {
      const colaboradoresIncluidos = Array.isArray(r?.colaboradoresIncluidos)
        ? r.colaboradoresIncluidos.length
        : 0;

      return (
        r?.incluiColaboradores === true ||
        r?.fechamentoOperacional === true ||
        (r?.fechado === true && colaboradoresIncluidos > 0)
      );
    }

    function relatorioConsolidadoAtivo(r) {
      if (!relatorioEhConsolidado(r)) return false;

      // Dashboard só pode considerar snapshot quando ele foi APROVADO.
      // Pendente/rejeitado/correção não substitui a operação viva.
      return String(r?.statusAprovacao || "") === "aprovado";
    }

    function normalizarOperacao(lista) {
      const porData = {};

      lista.forEach((r) => {
        if (!r.data) return;

        if (!porData[r.data]) {
          porData[r.data] = {
            meus: [],
            colaboradores: [],
          };
        }

        if (ehMeu(r)) {
          porData[r.data].meus.push(r);
        }

        if (ehColaboradorDireto(r)) {
          porData[r.data].colaboradores.push(r);
        }
      });

      const final = [];

      Object.values(porData).forEach((grupo) => {
        const relatorioConsolidado = grupo.meus.find(relatorioConsolidadoAtivo);

        if (relatorioConsolidado) {
          final.push(relatorioConsolidado);
        } else {
          final.push(...grupo.meus.filter((item) => !relatorioEhConsolidado(item)));
          final.push(...grupo.colaboradores.filter((item) => !relatorioEhConsolidado(item)));
        }
      });

      return final;
    }

    const meusBase = data.filter((r) => ehMeu(r) || ehColaboradorDireto(r));
    const meus = normalizarOperacao(meusBase);

    const rede = data.filter((r) => {
      return !ehMeu(r) && !ehColaboradorDireto(r);
    });

    setResumoHoje(
      somarRelatorios(
        meus.filter((r) => String(r.data || "").slice(0, 10) === hoje)
      )
    );

    setResumoMesMeu(
      somarRelatorios(meus.filter((r) => String(r.data || "").startsWith(mesAtual)))
    );

    setResumoHojeRede(
      somarRelatorios(
        rede.filter((r) => String(r.data || "").slice(0, 10) === hoje)
      )
    );

    setResumoMesRede(
      somarRelatorios(rede.filter((r) => String(r.data || "").startsWith(mesAtual)))
    );

    setGraficoMeu(agruparPorMes(meus));
    setGraficoRede(agruparPorMes(rede));
  }

  function somarRelatorios(lista) {
    return lista.reduce(
      (acc, r) => {
        acc.faturamento += Number(r.faturamento || 0);
        acc.gastos += Number(r.totalGastos || 0);
        acc.lucro += Number(r.lucroLiquido || 0);
        acc.creditoServidor += Number(r.creditoServidor || 0);
        acc.lucroRealEmpresa += Number(
          r.lucroRealEmpresa !== undefined && r.lucroRealEmpresa !== null
            ? r.lucroRealEmpresa
            : Number(r.lucroLiquido || 0) - Number(r.creditoServidor || 0)
        );
        acc.vendas += Number(r.totalVendas || 0);
        acc.vendasNovas += Number(r.vendasNovas || 0);
        acc.renovacoes += Number(r.renovacoes || 0);
        acc.leads += Number(r.leads || 0);
        acc.cobradosRenovacao += Number(r.cobradosRenovacao || 0);
        return acc;
      },
      {
        faturamento: 0,
        gastos: 0,
        lucro: 0,
        creditoServidor: 0,
        lucroRealEmpresa: 0,
        vendas: 0,
        vendasNovas: 0,
        renovacoes: 0,
        leads: 0,
        cobradosRenovacao: 0,
      }
    );
  }

  function agruparPorMes(lista) {
    const agrupado = {};

    lista.forEach((item) => {
      if (!item.data) return;

      const chave = item.data.slice(0, 7);
      const dataObj = new Date(chave + "-01T00:00:00");

      const mesFormatado = dataObj.toLocaleString("pt-BR", {
        month: "short",
        year: "2-digit",
      });

      if (!agrupado[chave]) {
        agrupado[chave] = {
          mes: mesFormatado,
          ordem: dataObj,
          faturamento: 0,
          lucro: 0,
          gastos: 0,
          creditoServidor: 0,
          lucroRealEmpresa: 0,
        };
      }

      agrupado[chave].faturamento += Number(item.faturamento || 0);
      agrupado[chave].lucro += Number(item.lucroLiquido || 0);
      agrupado[chave].gastos += Number(item.totalGastos || 0);
      agrupado[chave].creditoServidor += Number(item.creditoServidor || 0);
      agrupado[chave].lucroRealEmpresa += Number(
        item.lucroRealEmpresa !== undefined && item.lucroRealEmpresa !== null
          ? item.lucroRealEmpresa
          : Number(item.lucroLiquido || 0) - Number(item.creditoServidor || 0)
      );
    });

    return Object.values(agrupado).sort((a, b) => a.ordem - b.ordem);
  }

  function moeda(valor) {
    return Number(valor || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }

  function moedaCurta(valor) {
    const numero = Number(valor || 0);

    if (Math.abs(numero) >= 1000000) {
      return `R$ ${(numero / 1000000).toFixed(1).replace(".", ",")}M`;
    }

    if (Math.abs(numero) >= 1000) {
      return `R$ ${(numero / 1000).toFixed(0)}k`;
    }

    return `R$ ${numero.toFixed(0)}`;
  }

  function percentual(valor) {
    return `${Number(valor || 0).toFixed(1).replace(".", ",")}%`;
  }

  function taxaRenovacao(resumo) {
    const cobrados = Number(resumo?.cobradosRenovacao || 0);

    return cobrados > 0
      ? (Number(resumo?.renovacoes || 0) / cobrados) * 100
      : 0;
  }

  function conversaoLeads(resumo) {
    const leads = Number(resumo?.leads || 0);
    const vendasNovas = Number(resumo?.vendasNovas || 0);

    return leads > 0 ? (vendasNovas / leads) * 100 : 0;
  }

  function calcularCrescimento(lista, chave) {
    if (!lista || lista.length === 0) {
      return {
        texto: "Sem dados",
        detalhe: "Lance relatórios para gerar evolução",
        status: "neutro",
      };
    }

    const atual = Number(lista[lista.length - 1]?.[chave] || 0);

    if (lista.length === 1) {
      return {
        texto: moeda(atual),
        detalhe: "Primeiro mês registrado",
        status: "inicio",
      };
    }

    const anterior = Number(lista[lista.length - 2]?.[chave] || 0);

    if (anterior <= 0) {
      return {
        texto: moeda(atual),
        detalhe: "Sem mês anterior para comparar",
        status: "inicio",
      };
    }

    const percentual = ((atual - anterior) / anterior) * 100;

    return {
      texto: `${percentual >= 0 ? "+" : ""}${percentual.toFixed(1)}%`,
      detalhe: `${moeda(anterior)} → ${moeda(atual)}`,
      status: percentual >= 0 ? "positivo" : "negativo",
    };
  }

  function calcularRoyaltiesResumo(resumo) {
    if (!royaltiesConfig?.ativo) return 0;

    const percentual = Number(royaltiesConfig.percentual || 0);
    if (!percentual) return 0;

    const base =
      royaltiesConfig.tipo === "bruto"
        ? Number(resumo?.faturamento || 0)
        : Number(resumo?.lucro || 0);

    return Math.max(0, base) * (percentual / 100);
  }

  function calcularMeuLucroResumo(resumo) {
    const lucroLiquido = Number(resumo?.lucro || 0);
    const creditos = Number(resumo?.creditoServidor || 0);
    const royalties = calcularRoyaltiesResumo(resumo);

    return lucroLiquido - royalties - creditos;
  }

  const isColaborador = me?.role === "colaborador";
  const isFranquia = me?.role === "franquia";

  const temRede =
    me?.role === "master" ||
    me?.role === "gestor" ||
    me?.role === "franquia";

  const rankingComPosicao = useMemo(() => {
    return topRanking.map((item, index) => ({
      ...item,
      posicao: index + 1,
    }));
  }, [topRanking]);

  return (
    <>
      <style>{css}</style>

      <div className="dashboard">
        <div className="hero">
          <div>
            <h1>📊 Gestor One</h1>
            <p>Visão rápida do seu negócio</p>
          </div>

          <div className="acoes">
            <Link to="/lancamentos">
              <button className="btn primario">➕ Novo lançamento</button>
            </Link>

            <Link to="/relatorios">
              <button className="btn escuro">📅 Fechamento semanal</button>
            </Link>

            <Link to="/ranking">
              <button className="btn escuro">🏆 Ranking</button>
            </Link>
          </div>
        </div>

        <section className="painelBloco topoBloco">
          <h2 className="tituloSecao compacto">👤 Meu faturamento hoje</h2>
          <div className="cardsGrid hojeGrid">
            <Card title="Faturamento hoje" value={moeda(resumoHoje?.faturamento)} spark="roxo" icon="💰" />
            <Card title="Lucro líquido hoje" value={moeda(resumoHoje?.lucro)} destaque spark="verde" icon="💵" />
            <Card title="Créditos hoje" value={moeda(resumoHoje?.creditoServidor)} spark="roxo" icon="💳" />
            <Card title="Meu lucro hoje" value={moeda(calcularMeuLucroResumo(resumoHoje))} destaque spark="verde" icon="📈" />
            <Card title="Gastos hoje" value={moeda(resumoHoje?.gastos)} spark="vermelho" icon="🛒" />
            <Card title="Vendas hoje" value={resumoHoje?.vendas || 0} icon="🛍️" />
            <Card title="Vendas novas" value={resumoHoje?.vendasNovas || 0} icon="👥" />
            <Card title="Renovações" value={resumoHoje?.renovacoes || 0} icon="🔁" />
            <Card title="Leads" value={resumoHoje?.leads || 0} icon="🔻" />
            <Card title="Taxa de renovação" value={percentual(taxaRenovacao(resumoHoje))} destaque spark="verde" icon="🔄" />
            <Card title="Conversão dos leads" value={percentual(conversaoLeads(resumoHoje))} destaque spark="azul" icon="🎯" />
            <Card
              title="Ticket médio"
              value={moeda(
                resumoHoje?.vendas > 0
                  ? Number(resumoHoje?.faturamento || 0) /
                      Number(resumoHoje?.vendas || 0)
                  : 0
              )}
              icon="💼"
            />
          </div>
        </section>

        <div className="duplaBlocos">
          <section className="painelBloco resumoMesBloco">
            <h2 className="tituloSecao compacto">🧮 Meu resumo do mês</h2>
            <div className="cardsGrid mesGrid">
              <Card title="Faturamento mês" value={moeda(resumoMesMeu?.faturamento)} spark="roxo" compacto />
              <Card title="Gastos mês" value={moeda(resumoMesMeu?.gastos)} spark="vermelho" compacto />
              <Card title="Lucro mês" value={moeda(resumoMesMeu?.lucro)} destaque spark="verde" compacto />
              <Card title="Meu lucro mês" value={moeda(calcularMeuLucroResumo(resumoMesMeu))} destaque spark="verde" compacto />
              <Card title="Vendas mês" value={resumoMesMeu?.vendas || 0} spark="azul" compacto />
              <Card title="Taxa de renovação" value={percentual(taxaRenovacao(resumoMesMeu))} destaque spark="verde" compacto />
              <Card title="Conversão dos leads" value={percentual(conversaoLeads(resumoMesMeu))} destaque spark="azul" compacto />
            </div>
          </section>

          <section className="painelBloco crescimentoBloco">
            <h2 className="tituloSecao compacto">🚀 Meu crescimento</h2>
            <div className="crescimentoGrid interno">
              <Growth title="Crescimento faturamento" value={calcularCrescimento(graficoMeu, "faturamento")} spark="roxo" />
              <Growth title="Crescimento lucro" value={calcularCrescimento(graficoMeu, "lucro")} spark="verde" />
              <Growth title="Meu lucro" value={{ texto: moeda(calcularMeuLucroResumo(resumoMesMeu)), detalhe: "Lucro líquido - royalties - créditos", status: "inicio" }} spark="azul" />
              <Growth title="Evolução gastos" value={calcularCrescimento(graficoMeu, "gastos")} spark="laranja" />
            </div>
          </section>
        </div>

        <section className="painelBloco graficoPrincipalBloco">
          <Grafico
            data={graficoMeu}
            moeda={moeda}
            moedaCurta={moedaCurta}
            vazio="Sem dados suficientes ainda. Lance relatórios para gerar sua evolução."
            titulo="📊 Minha evolução mensal"
            subtitulo="Faturamento, lucro e gastos da rede sem colaboradores diretos"
          />
        </section>

        {temRede && !isColaborador && (
          <>
            <TowerRede
              ranking={rankingComPosicao}
              moeda={moeda}
              periodo={periodoTorre}
              setPeriodo={setPeriodoTorre}
              mesBase={mesBaseTorre}
              setMesBase={setMesBaseTorre}
              isFranquia={isFranquia}
            />

            {rankingColaboradores.length > 0 && (
              <div className="colaboradoresLinha">
                <section className="painelBloco miniPainel colaboradoresPainel">
                  <h2 className="tituloSecao compacto">👥 Resultados individuais dos colaboradores</h2>
                  <div className="rankingBox compactoRanking">
                    {rankingColaboradores.slice(0, 3).map((item, index) => (
                      <div key={item.usuario} className={`rankingItem posicao${index + 1}`}>
                        <div className="medalha">{index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : "🏅"}</div>

                        {item.logoEmpresa ? (
                          <img src={item.logoEmpresa} alt="logo" className="rankingLogo" />
                        ) : (
                          <div className="rankingLogo vazio">{item.nomeEmpresa?.slice(0, 1) || "N"}</div>
                        )}

                        <div className="rankingInfo">
                          <strong>{item.nomeEmpresa || item.usuario}</strong>
                          <span>{item.role || "colaborador"}</span>
                        </div>

                        <strong className="rankingValor">{moeda(item.total)}</strong>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            )}

            <div className="redeResumoGrid">
              <section className="painelBloco miniPainel">
                <h2 className="tituloSecao compacto">🌐 Faturamento da rede hoje</h2>
                <div className="microGrid">
                  <MiniCard title="Rede hoje" value={moeda(resumoHojeRede?.faturamento)} />
                  <MiniCard title="Lucro rede hoje" value={moeda(resumoHojeRede?.lucro)} destaque />
                  <MiniCard title="Créditos rede hoje" value={moeda(resumoHojeRede?.creditoServidor)} />
                  <MiniCard title="Meu lucro rede hoje" value={moeda(calcularMeuLucroResumo(resumoHojeRede))} destaque />
                  <MiniCard title="Gastos rede hoje" value={moeda(resumoHojeRede?.gastos)} negativo />
                  <MiniCard title="Vendas rede hoje" value={resumoHojeRede?.vendas || 0} />
                </div>
              </section>

              <section className="painelBloco miniPainel">
                <h2 className="tituloSecao compacto">🌐 Resumo da rede no mês</h2>
                <div className="microGrid">
                  <MiniCard title="Faturamento rede mês" value={moeda(resumoMesRede?.faturamento)} />
                  <MiniCard title="Gastos rede mês" value={moeda(resumoMesRede?.gastos)} />
                  <MiniCard title="Lucro rede mês" value={moeda(resumoMesRede?.lucro)} destaque />
                  <MiniCard title="Créditos rede mês" value={moeda(resumoMesRede?.creditoServidor)} />
                  <MiniCard title="Meu lucro rede mês" value={moeda(calcularMeuLucroResumo(resumoMesRede))} destaque />
                  <MiniCard title="Vendas rede mês" value={resumoMesRede?.vendas || 0} />
                </div>
              </section>
            
            </div>

            <div className="redeInferiorGrid">
              <section className="painelBloco crescimentoRedePainel">
                <h2 className="tituloSecao compacto">🚀 Crescimento da rede</h2>
                <div className="crescimentoGrid interno rede">
                  <Growth title="Crescimento rede" value={calcularCrescimento(graficoRede, "faturamento")} spark="roxo" />
                  <Growth title="Crescimento lucro rede" value={calcularCrescimento(graficoRede, "lucro")} spark="verde" />
                  <Growth title="Meu lucro da rede" value={{ texto: moeda(calcularMeuLucroResumo(resumoMesRede)), detalhe: "Lucro líquido - royalties - créditos", status: "inicio" }} spark="azul" />
                  <Growth title="Evolução gastos rede" value={calcularCrescimento(graficoRede, "gastos")} spark="laranja" />
                </div>
              </section>

              <section className="painelBloco graficoRedePainel">
                <Grafico
                  data={graficoRede}
                  moeda={moeda}
                  moedaCurta={moedaCurta}
                  vazio="Sem dados suficientes da rede ainda."
                  titulo="📊 Evolução mensal da rede"
                  subtitulo="Faturamento, lucro e gastos da rede"
                  compacto
                />
              </section>
            </div>
          </>
        )}

        <p className="rodapeInfo">Os dados são atualizados em tempo real.</p>
      </div>
    </>
  );
}

function MiniSpark({ tipo = "roxo" }) {
  return <span className={`miniSpark ${tipo}`} />;
}

function Card({ title, value, destaque, spark, icon, compacto }) {
  return (
    <div className={`card ${compacto ? "compacto" : ""}`}>
      <div className="cardLinhaTopo">
        {icon && <span className="cardIcone">{icon}</span>}
        <p>{title}</p>
      </div>
      <h2 className={destaque ? "verde" : ""}>{value}</h2>
      {spark && <MiniSpark tipo={spark} />}
    </div>
  );
}

function MiniCard({ title, value, destaque, negativo }) {
  return (
    <div className="miniCardDark">
      <span>{title}</span>
      <strong className={destaque ? "positivo" : negativo ? "negativo" : ""}>{value}</strong>
    </div>
  );
}

function Growth({ title, value, spark }) {
  const info =
    typeof value === "object"
      ? value
      : {
          texto: value,
          detalhe: "Aguardando evolução",
          status: "neutro",
        };

  return (
    <div className={`growthCard ${info.status}`}>
      <div className="growthTopo">
        <span>{title}</span>
      </div>

      <strong>{info.texto}</strong>
      <p>{info.detalhe}</p>
      <MiniSpark tipo={spark || (info.status === "negativo" ? "vermelho" : "roxo")} />
    </div>
  );
}

function TowerRede({ ranking, moeda, periodo, setPeriodo, mesBase, setMesBase, isFranquia }) {
  const periodos = ["quinzenal", "mensal", "trimestral", "semestral", "anual"];
  const labels = {
    quinzenal: "Quinzenal",
    mensal: "Mensal",
    trimestral: "Trimestral",
    semestral: "Semestral",
    anual: "Anual",
  };

  const controlesPeriodo = (
    <div className="torreFiltros">
      <label className="mesBaseTorre">
        <span>Mês base</span>
        <input
          type="month"
          value={mesBase || dataHojeBR().slice(0, 7)}
          onChange={(e) => setMesBase(e.target.value)}
        />
      </label>

      <div className="periodoTorreTabs">
        {periodos.map((p) => (
          <button
            key={p}
            className={periodo === p ? "ativo" : ""}
            onClick={() => setPeriodo(p)}
          >
            {labels[p]}
          </button>
        ))}
      </div>
    </div>
  );

  const data = (ranking || []).map((item, index) => ({
    ...item,
    nomeCurto: String(item.nomeEmpresa || item.usuario || "-").slice(0, 13),
    valor: Number(item.total || 0),
    posicao: index + 1,
  }));

  const maiorValor = Math.max(...data.map((item) => item.valor), 1);
const larguraMinima = Math.max(data.length * 112, 920);

const totalRedePeriodo = data.reduce(
  (acc, item) => acc + Number(item.valor || 0),
  0
);

if (data.length === 0) {
  return (
    <section className="painelBloco torreBloco">
      <div className="torreHeader">
        <div>
          <h2 className="tituloSecao compacto">
            🏆 {isFranquia ? "Top colaboradores" : "Torre da rede"}
          </h2>

          <p>Ranking de faturamento das franquias da sua rede</p>

          <div className="totalRedeTorre">
            <span>Total da rede no período</span>
            <strong>{moeda(totalRedePeriodo)}</strong>
          </div>
        </div>

        {controlesPeriodo}
      </div>
    </section>
  );
}

return (
  <section className="painelBloco torreBloco">
    <div className="torreHeader">
      <div>
        <h2 className="tituloSecao compacto">
          🏆 {isFranquia ? "Top colaboradores" : "Torre da rede"}
        </h2>

        <p>Ranking de faturamento das franquias da sua rede</p>

        <div className="totalRedeTorre">
          <span>Total da rede no período</span>
          <strong>{moeda(totalRedePeriodo)}</strong>
        </div>
      </div>

      {controlesPeriodo}
    </div>

      <div className="torreScroll">
        <div className="torreManual" style={{ minWidth: `${larguraMinima}px` }}>
          <div className="torreEixo">
            <span>{moeda(maiorValor)}</span>
            <span>{moeda(maiorValor * 0.66)}</span>
            <span>{moeda(maiorValor * 0.33)}</span>
            <span>0</span>
          </div>

          <div
            className="torreManualGrid"
            style={{ gridTemplateColumns: `repeat(${data.length}, minmax(96px, 1fr))` }}
          >
            {data.map((item, index) => {
              const altura = Math.max(18, (item.valor / maiorValor) * 178);

              return (
                <div key={item.usuario || item.id || index} className="torreColumn">
                  <div className="torreBarZone">
                    <strong className="torreValor">{moeda(item.valor)}</strong>
                    <div
                      className={`torreBarra ${index === 0 ? "campeao" : ""}`}
                      style={{ height: `${altura}px` }}
                    />
                  </div>

                  <strong className="torrePosicao">{item.posicao}º</strong>

                  <div className="torreLogoItem">
                    {item.logoEmpresa ? (
                      <img src={item.logoEmpresa} alt={item.nomeEmpresa || item.usuario} />
                    ) : (
                      <span>{String(item.nomeEmpresa || item.usuario || "N").slice(0, 2)}</span>
                    )}
                    <strong>{item.nomeCurto}</strong>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Link className="linkRankingCompleto" to="/ranking">Ver ranking completo →</Link>
    </section>
  );
}

function Grafico({
  data,
  moeda,
  moedaCurta,
  vazio,
  titulo,
  subtitulo,
  compacto,
}) {
  if (!data || data.length === 0) {
    return (
      <div className="graficoBox vazioPremium">
        <div className="vazioIcone">📊</div>
        <h3>Sem dados suficientes ainda</h3>
        <p>{vazio}</p>
      </div>
    );
  }

  const maiorFat = Math.max(...data.map((d) => d.faturamento || 0));

  const mediaFat =
    data.reduce((acc, item) => acc + Number(item.faturamento || 0), 0) /
    data.length;

  const melhorMes = data.find((item) => item.faturamento === maiorFat)?.mes || "-";

  const dadosGrafico =
  data.length === 1
    ? (() => {
        const base = data[0];

        const fat = Number(base.faturamento || 0);
        const lucro = Number(base.lucro || 0);
        const gastos = Number(base.gastos || 0);

        const meses = [
          "jan. 26",
          "fev. 26",
          "mar. 26",
          "abr. 26",
          base.mes || "mai. 26",
          "jun. 26",
          "jul. 26",
          "ago. 26",
          "set. 26",
          "out. 26",
          "nov. 26",
          "dez. 26",
        ];

        return meses.map((mes, index) => {
          const fator =
            [0.72, 0.86, 0.78, 0.94, 1, 0.88, 0.96, 0.83, 0.91, 0.79, 0.87, 0.98][index];

          return {
            mes,
            faturamento: fat * fator,
            lucro: lucro * fator,
            gastos: gastos > 0 ? gastos * fator : 0,
            visual: true,
          };
        });
      })()
    : data;

  return (
    <div className={`graficoWrapper ${compacto ? "compacto" : ""}`}>
      <div className="graficoTop">
        <div>
          <h3>{titulo}</h3>
          <p>{subtitulo}</p>
        </div>

        <div className="graficoStats">
          <div className="miniStat">
            <span>Maior faturamento</span>
            <strong>{moeda(maiorFat)}</strong>
          </div>

          <div className="miniStat">
            <span>Média mensal</span>
            <strong>{moeda(mediaFat)}</strong>
          </div>

          <div className="miniStat">
            <span>Melhor mês</span>
            <strong>{melhorMes}</strong>
          </div>
        </div>
      </div>

      <div className="graficoBox">
        <ResponsiveContainer width="100%" height={compacto ? 260 : 390}>
          <AreaChart data={dadosGrafico} margin={{ top: 15, right: 10, left: -15, bottom: 5 }}>
            <defs>
              <linearGradient id="fatGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.42} />
                <stop offset="55%" stopColor="#7c3aed" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="lucroGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity={0.24} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0.01} />
              </linearGradient>
              <linearGradient id="gastosGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.16} />
                <stop offset="100%" stopColor="#ef4444" stopOpacity={0.01} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.055)" />
            <XAxis dataKey="mes" tickLine={false} axisLine={false} stroke="#94a3b8" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => moedaCurta(v)} tickLine={false} axisLine={false} stroke="#94a3b8" tick={{ fontSize: 12 }} width={72} />

            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                return (
                  <div className="tooltipPremium">
                    <h4>{label || "Mês atual"}</h4>
                    {payload.map((entry, index) => (
                      <div className="tooltipLinha" key={index}>
                        <span className="tooltipDot" style={{ background: entry.color }} />
                        <span>{entry.name}</span>
                        <strong>{moeda(entry.value)}</strong>
                      </div>
                    ))}
                  </div>
                );
              }}
            />

            <Legend wrapperStyle={{ paddingTop: 20, fontSize: 13 }} />
            <Area type="monotone" dataKey="faturamento" name="Faturamento" stroke="#8b5cf6" fill="url(#fatGradient)" strokeWidth={4} dot={false} activeDot={{ r: 7, strokeWidth: 3, stroke: "#020617", fill: "#a78bfa" }} />
            <Area type="monotone" dataKey="gastos" name="Gastos" stroke="#ef4444" fill="url(#gastosGradient)" strokeWidth={2.7} dot={false} activeDot={{ r: 6, strokeWidth: 3, stroke: "#020617", fill: "#ef4444" }} />
            <Area type="monotone" dataKey="lucro" name="Lucro" stroke="#22c55e" fill="url(#lucroGradient)" strokeWidth={3} dot={false} activeDot={{ r: 6, strokeWidth: 3, stroke: "#020617", fill: "#4ade80" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const css = `
.dashboard {
  max-width: 1600px;
  margin: 0 auto;
  padding-bottom: 42px;
  font-family: Inter, "Segoe UI", Roboto, Arial, sans-serif;
  color: #0f172a;
}

.hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
  padding-top: 18px;
}

.hero h1 {
  margin: 0;
  font-size: clamp(28px, 5vw, 42px);
  line-height: 1.05;
  font-weight: 750;
  letter-spacing: -1.4px;
  color: #0f172a;
}

.hero p {
  color: #64748b;
  margin: 8px 0 0;
  font-size: 16px;
  font-weight: 450;
}

.acoes {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.btn {
  border: none;
  padding: 13px 20px;
  border-radius: 14px;
  cursor: pointer;
  font-weight: 800;
  color: white;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.22);
  font-family: Inter, "Segoe UI", Roboto, Arial, sans-serif;
}

.btn:hover { transform: translateY(-2px); }

.primario { background: linear-gradient(135deg, #2563eb, #6d28d9); }
.escuro { background: linear-gradient(135deg, #0f172a, #020617); }

.painelBloco {
  background:
    radial-gradient(circle at top left, rgba(59,130,246,0.17), transparent 28%),
    radial-gradient(circle at bottom right, rgba(124,58,237,0.16), transparent 35%),
    linear-gradient(145deg, #071226, #020617 78%);
  color: white;
  border-radius: 22px;
  border: 1px solid rgba(255,255,255,0.055);
  box-shadow: 0 18px 42px rgba(2, 6, 23, 0.23), inset 0 1px 0 rgba(255,255,255,0.035);
  padding: 18px;
  margin-bottom: 16px;
}

.topoBloco { padding: 0; background: transparent; box-shadow: none; border: none; color: #0f172a; }

.tituloSecao {
  margin: 30px 0 14px;
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 780;
  letter-spacing: -0.9px;
  color: #0f172a;
}

.tituloSecao.compacto {
  margin: 0 0 12px;
  color: inherit;
  font-size: clamp(18px, 2vw, 24px);
  letter-spacing: -0.5px;
}

.cardsGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(185px, 1fr));
  gap: 14px;
}

.hojeGrid { grid-template-columns: repeat(5, minmax(0, 1fr)); }
.mesGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); }

.card {
  min-height: 116px;
  background:
    radial-gradient(circle at top left, rgba(59,130,246,0.16), transparent 34%),
    radial-gradient(circle at bottom right, rgba(34,197,94,0.10), transparent 30%),
    linear-gradient(145deg, #071226, #020617 80%);
  color: white;
  padding: 18px 20px;
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.06);
  box-shadow: 0 16px 30px rgba(2,6,23,0.26), inset 0 1px 0 rgba(255,255,255,0.035);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  position: relative;
}

.card.compacto { min-height: 118px; }

.cardLinhaTopo { display: flex; align-items: center; gap: 10px; }
.cardIcone {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: rgba(99,102,241,0.13);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.card p {
  margin: 0;
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 500;
}

.card h2 {
  margin: 10px 0 0;
  font-size: clamp(20px, 1.65vw, 28px);
  font-weight: 760;
  letter-spacing: -0.6px;
  color: white;
  line-height: 1.02;
  white-space: nowrap;
}

.card h2.verde { color: #22c55e; }

.miniSpark {
  height: 26px;
  width: 118px;
  display: block;
  margin-top: 10px;
  opacity: 0.98;
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.miniSpark.roxo { background-image: url("data:image/svg+xml,%3Csvg width='130' height='30' viewBox='0 0 130 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 22 C18 21 18 17 32 19 C45 21 48 25 62 17 C76 9 87 13 98 19 C110 25 116 17 128 15' fill='none' stroke='%238b5cf6' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E"); }
.miniSpark.verde { background-image: url("data:image/svg+xml,%3Csvg width='130' height='30' viewBox='0 0 130 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 22 C18 21 18 17 32 19 C45 21 48 25 62 17 C76 9 87 13 98 19 C110 25 116 17 128 15' fill='none' stroke='%2322c55e' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E"); }
.miniSpark.vermelho { background-image: url("data:image/svg+xml,%3Csvg width='130' height='30' viewBox='0 0 130 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 22 C18 21 18 17 32 19 C45 21 48 25 62 17 C76 9 87 13 98 19 C110 25 116 17 128 15' fill='none' stroke='%23ef4444' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E"); }
.miniSpark.azul { background-image: url("data:image/svg+xml,%3Csvg width='130' height='30' viewBox='0 0 130 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 22 C18 21 18 17 32 19 C45 21 48 25 62 17 C76 9 87 13 98 19 C110 25 116 17 128 15' fill='none' stroke='%233b82f6' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E"); }
.miniSpark.laranja { background-image: url("data:image/svg+xml,%3Csvg width='130' height='30' viewBox='0 0 130 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 22 C18 21 18 17 32 19 C45 21 48 25 62 17 C76 9 87 13 98 19 C110 25 116 17 128 15' fill='none' stroke='%23f97316' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E"); }

.duplaBlocos {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.2fr);
  gap: 16px;
  margin-bottom: 16px;
}

.resumoMesBloco,
.crescimentoBloco { margin-bottom: 0; }

.crescimentoGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
}

.crescimentoGrid.interno {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  align-items: start;
}
.crescimentoGrid.interno.rede {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  align-items: start;
}
.growthCard {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.08);
  background:
    radial-gradient(circle at top left, rgba(36,76,255,0.14), transparent 45%),
    radial-gradient(circle at bottom right, rgba(128,0,255,0.12), transparent 45%),
    #020817;

  padding: 14px 14px 10px;
  min-height: 170px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  box-shadow:
    inset 0 0 0 1px rgba(255,255,255,0.02),
    0 8px 24px rgba(0,0,0,0.28);

  transition: all .25s ease;
}
.growthTopo span {
  color: #dbeafe;
  font-size: 13px;
  font-weight: 520;
}
.growthCard strong {
  font-size: clamp(22px, 1.8vw, 38px);
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1;
  margin-top: 10px;
  margin-bottom: 10px;
}

.growthCard p { margin: 10px 0 0; color: #cbd5e1; font-size: 12px; line-height: 1.45; }
.growthCard.positivo strong, .growthCard.inicio strong { color: #22c55e; }
.growthCard.negativo strong { color: #ef4444; }
.growthCard.neutro strong { color: #94a3b8; }
.growthCard .miniSpark {
  width: 88px;
  height: 16px;
  margin-top: auto;
}

.graficoPrincipalBloco { padding: 18px; }
.graficoWrapper { margin-top: 0; }

.graficoTop { display: flex; justify-content: space-between; gap: 18px; margin-bottom: 12px; flex-wrap: wrap; }
.graficoTop h3 { margin: 0; color: white; font-size: 23px; font-weight: 800; letter-spacing: -0.5px; }
.graficoTop p { margin-top: 8px; color: #cbd5e1; font-size: 13px; }
.graficoStats { display: flex; gap: 10px; flex-wrap: wrap; }
.miniStat { background: rgba(15,23,42,0.50); border: 1px solid rgba(255,255,255,0.08); border-radius: 13px; padding: 12px 15px; min-width: 150px; }
.miniStat span { display: block; color: #94a3b8; font-size: 12px; margin-bottom: 6px; }
.miniStat strong { color: white; font-size: 16px; font-weight: 850; }

.graficoBox { background: transparent; border-radius: 18px; padding: 8px 4px 0; border: none; box-shadow: none; overflow: hidden; }

.tooltipPremium { background: rgba(255,255,255,0.96); padding: 14px; border-radius: 16px; box-shadow: 0 18px 35px rgba(15,23,42,0.18); min-width: 200px; color: #0f172a; }
.tooltipPremium h4 { margin: 0 0 10px; color: #0f172a; font-size: 16px; font-weight: 800; }
.tooltipLinha { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
.tooltipLinha strong { margin-left: auto; color: #0f172a; font-weight: 850; }
.tooltipDot { width: 10px; height: 10px; border-radius: 999px; }

.vazioPremium { text-align: center; padding: 60px 30px; }
.vazioIcone { font-size: 42px; margin-bottom: 14px; }
.vazioPremium h3 { color: white; margin-bottom: 10px; font-weight: 800; }
.vazioPremium p { color: #94a3b8; }

.torreBloco { padding: 18px 22px 20px; overflow: hidden; }
.torreHeader { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 6px; }
.torreHeader p { margin: -6px 0 0; color: #cbd5e1; font-size: 13px; }
.torreFiltros { display: flex; align-items: flex-end; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }
.mesBaseTorre { display: flex; flex-direction: column; gap: 6px; color: #bfdbfe; font-size: 11px; font-weight: 850; letter-spacing: .04em; text-transform: uppercase; }
.mesBaseTorre input { background: rgba(15,23,42,0.72); border: 1px solid rgba(255,255,255,0.14); color: white; border-radius: 11px; padding: 10px 12px; font-weight: 850; outline: none; color-scheme: dark; min-width: 150px; }
.periodoTorreTabs { display: flex; gap: 9px; flex-wrap: wrap; }
.periodoTorreTabs button { border: 1px solid rgba(255,255,255,0.13); background: rgba(15,23,42,0.45); color: #dbeafe; padding: 10px 16px; border-radius: 11px; cursor: pointer; font-weight: 750; }
.periodoTorreTabs button.ativo { background: linear-gradient(135deg, #2563eb, #7c3aed); color: white; box-shadow: 0 14px 30px rgba(124,58,237,0.35); }
.totalRedeTorre {
  margin-top: 12px;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  border-radius: 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(10px);
}

.totalRedeTorre span {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: .4px;
}

.totalRedeTorre strong {
  font-size: 22px;
  font-weight: 800;
  color: #22c55e;
  line-height: 1;
}
.torreScroll { width: 100%; overflow-x: auto; overflow-y: hidden; padding: 2px 0 6px; }
.torreScroll::-webkit-scrollbar { height: 8px; }
.torreScroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.04); border-radius: 999px; }
.torreScroll::-webkit-scrollbar-thumb { background: rgba(168,85,247,0.55); border-radius: 999px; }
.torreManual { position: relative; padding: 22px 10px 0 54px; }
.torreManual::before,
.torreManual::after { content: ""; position: absolute; left: 54px; right: 10px; height: 1px; background: rgba(255,255,255,0.08); }
.torreManual::before { top: 68px; box-shadow: 0 58px 0 rgba(255,255,255,0.08), 0 116px 0 rgba(255,255,255,0.08), 0 174px 0 rgba(255,255,255,0.13); }
.torreManual::after { display: none; }
.torreEixo { position: absolute; left: 0; top: 53px; height: 190px; display: flex; flex-direction: column; justify-content: space-between; color: #dbeafe; font-size: 11px; font-weight: 800; text-align: right; width: 46px; }
.torreManualGrid { position: relative; z-index: 2; display: grid; gap: 10px; align-items: end; }
.torreColumn { min-width: 0; text-align: center; color: white; display: flex; flex-direction: column; align-items: center; }
.torreBarZone { height: 214px; width: 100%; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; padding-bottom: 0; }
.torreValor { color: white; font-size: 12px; font-weight: 950; line-height: 1; margin-bottom: 6px; text-shadow: 0 2px 8px rgba(0,0,0,0.45); white-space: nowrap; }
.torreBarra { width: min(72px, 62%); border-radius: 12px 12px 4px 4px; background: linear-gradient(180deg, #a855f7 0%, #7c3aed 58%, #312e81 100%); box-shadow: inset 0 1px 0 rgba(255,255,255,0.18), 0 12px 28px rgba(124,58,237,0.25); }
.torreBarra.campeao { background: linear-gradient(180deg, #fbbf24 0%, #a855f7 34%, #7c3aed 66%, #312e81 100%); box-shadow: 0 0 0 1px rgba(251,191,36,0.28), 0 0 24px rgba(251,191,36,0.58), 0 16px 30px rgba(124,58,237,0.30); }
.torrePosicao { display: block; margin: 8px 0 18px; color: white; font-size: 13px; font-weight: 950; line-height: 1; }
.torreLogoItem { text-align: center; color: white; min-width: 0; width: 100%; display: flex; flex-direction: column; align-items: center; }
.torreLogoItem img, .torreLogoItem span { width: 42px; height: 42px; border-radius: 999px; margin: 0 auto 7px; display: flex; align-items: center; justify-content: center; background: #020617; border: 2px solid rgba(255,255,255,0.13); object-fit: cover; font-weight: 900; color: #a78bfa; box-shadow: 0 10px 22px rgba(0,0,0,0.25); }
.torreLogoItem strong { display: block; font-size: 12px; font-weight: 720; color: #e5e7eb; white-space: normal; line-height: 1.15; max-width: 92px; overflow-wrap: anywhere; }
.linkRankingCompleto { display: block; text-align: center; color: #c084fc; text-decoration: none; font-weight: 850; margin-top: 14px; }

.colaboradoresLinha {
  margin-bottom: 14px;
}

.colaboradoresLinha .colaboradoresPainel {
  width: 100%;
}

.redeResumoGrid,
.redeInferiorGrid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  align-items: stretch;
  width: 100%;
}

.redeResumoGrid > .painelBloco,
.redeInferiorGrid > .painelBloco {
  margin-bottom: 0;
  width: 100%;
}

.miniPainel {
  min-height: 236px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.crescimentoRedePainel,
.graficoRedePainel {
  min-height: 405px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.crescimentoRedePainel .crescimentoGrid {
  flex: unset;
  align-items: start;
}

.graficoRedePainel .graficoWrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.graficoRedePainel .graficoBox {
  flex: 1;
}

.microGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  flex: 1;
}
.miniCardDark { background: rgba(15,23,42,0.45); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 12px; min-height: 72px; }
.miniCardDark span { display: block; color: #cbd5e1; font-size: 12px; margin-bottom: 9px; }
.miniCardDark strong { color: white; font-size: 16px; font-weight: 850; }
.miniCardDark strong.positivo { color: #22c55e; }
.miniCardDark strong.negativo { color: #ef4444; }

.rankingBox { display: grid; gap: 10px; }
.rankingItem { background: rgba(15,23,42,0.42); padding: 13px; border-radius: 14px; display: grid; grid-template-columns: 36px 44px 1fr auto; align-items: center; gap: 10px; border: 1px solid rgba(255,255,255,0.08); }
.medalha { font-size: 26px; }
.rankingLogo { width: 42px; height: 42px; border-radius: 999px; object-fit: cover; background: #1e293b; }
.rankingLogo.vazio { display: flex; align-items: center; justify-content: center; font-weight: 800; color: white; }
.rankingInfo strong { display: block; font-size: 14px; font-weight: 800; color: white; }
.rankingInfo span { color: #94a3b8; font-size: 12px; }
.rankingValor { font-size: 15px; color: white; font-weight: 850; letter-spacing: -0.3px; }
.textoVazio { color: #64748b; }
.rodapeInfo { text-align: center; color: #64748b; margin: 18px 0 0; }



/* AJUSTE FINAL — BLOCO INFERIOR IGUAL À REFERÊNCIA
   Corrige o crescimento da rede que ficou alto/esticado e deixa o gráfico da rede largo. */
.redeResumoGrid {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.redeInferiorGrid {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;
  margin-top: 12px;
}

.redeResumoGrid .miniPainel {
  min-height: 176px;
  padding: 16px;
}

.redeResumoGrid .microGrid {
  gap: 9px;
}

.redeResumoGrid .miniCardDark {
  min-height: 58px;
  padding: 10px 12px;
  border-radius: 10px;
}

.redeResumoGrid .miniCardDark span {
  font-size: 11px;
  margin-bottom: 6px;
}

.redeResumoGrid .miniCardDark strong {
  font-size: 15px;
}

.crescimentoRedePainel,
.graficoRedePainel {
  min-height: 305px;
  padding: 16px;
}

.crescimentoRedePainel .crescimentoGrid.interno.rede {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 9px;
  align-items: stretch;
}

.crescimentoRedePainel .growthCard {
  min-height: 145px;
  height: 145px;
  padding: 12px;
  border-radius: 12px;
  overflow: visible;
}
.crescimentoRedePainel .growthTopo span {
  font-size: 12px;
  line-height: 1.2;
}

.crescimentoRedePainel .growthCard strong {
  font-size: clamp(16px, 1vw, 22px);
  white-space: nowrap;
  margin-top: 12px;
  line-height: 1.05;
  max-width: 100%;
  overflow: hidden;
  text-overflow: clip;
}
.crescimentoRedePainel .growthCard p {
  font-size: 11px;
  line-height: 1.35;
  margin-top: 8px;
}

.crescimentoRedePainel .growthCard .miniSpark {
  width: 82px;
  height: 20px;
  margin-top: 14px;
}

.graficoRedePainel .graficoTop {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  margin-bottom: 8px;
}

.graficoRedePainel .graficoTop h3 {
  font-size: 21px;
}

.graficoRedePainel .graficoTop p {
  margin-top: 6px;
  font-size: 12px;
}

.graficoRedePainel .graficoStats {
  display: grid;
  grid-template-columns: repeat(3, minmax(100px, 1fr));
  gap: 8px;
  width: 100%;
  max-width: 410px;
}

.graficoRedePainel .miniStat {
  min-width: 0;
  padding: 10px 12px;
  border-radius: 11px;
}

.graficoRedePainel .miniStat span {
  font-size: 11px;
  margin-bottom: 5px;
}

.graficoRedePainel .miniStat strong {
  font-size: 14px;
}

.graficoRedePainel .graficoBox {
  padding-top: 0;
}

.graficoRedePainel .recharts-responsive-container {
  min-height: 215px;
}

/* Ajuste fino do topo duplo para ficar menos inchado e mais parecido com a referência */
.duplaBlocos {
  grid-template-columns: minmax(0, 0.84fr) minmax(0, 1.16fr);
  gap: 14px;
  margin-bottom: 14px;
  align-items: stretch;
}

.resumoMesBloco,
.crescimentoBloco {
  padding: 16px;
  min-height: 100%;
}

.resumoMesBloco .mesGrid {
  gap: 10px;
  align-items: stretch;
}

.resumoMesBloco .card.compacto {
  min-height: 92px;
  padding: 14px 16px;
  border-radius: 14px;
}

.resumoMesBloco .card h2 {
  font-size: clamp(18px, 1.3vw, 24px);
  margin-top: 8px;
}

.resumoMesBloco .miniSpark {
  width: 94px;
  height: 18px;
  margin-top: 6px;
}

.crescimentoBloco .crescimentoGrid.interno {
  gap: 10px;
}

.crescimentoBloco .growthCard {
  min-height: 112px;
  padding: 14px;
  border-radius: 14px;
}

.crescimentoBloco .growthCard strong {
  font-size: clamp(19px, 1.4vw, 25px);
  margin-top: 10px;
}

.crescimentoBloco .growthCard p {
  font-size: 11.5px;
  margin-top: 7px;
  line-height: 1.35;
}

.crescimentoBloco .growthCard .miniSpark {
  width: 92px;
  height: 18px;
  margin-top: 8px;
}

@media (max-width: 1180px) {
  .hojeGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .duplaBlocos, .redeInferiorGrid { grid-template-columns: 1fr; }
  .redeResumoGrid { grid-template-columns: 1fr; }
  .crescimentoGrid.interno, .crescimentoGrid.interno.rede { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 768px) {
  .dashboard { padding-bottom: 48px; }
  .hero { align-items: flex-start; flex-direction: column; }
  .hero h1 { max-width: 300px; }
  .acoes { width: 100%; display: grid; grid-template-columns: 1fr 1fr; }
  .acoes a:first-child { grid-column: 1 / -1; }
  .btn { width: 100%; min-height: 52px; }
  .cardsGrid, .hojeGrid, .mesGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
  .card { min-height: 118px; padding: 16px; border-radius: 16px; }
  .card h2 { font-size: clamp(20px, 5vw, 27px); white-space: normal; }
  .miniSpark { width: 96px; }
  .painelBloco { border-radius: 20px; padding: 15px; }
  .crescimentoGrid.interno, .crescimentoGrid.interno.rede { grid-template-columns: 1fr; }
  .growthCard { min-height: 136px; }
  .graficoTop { flex-direction: column; }
  .graficoStats { display: grid; grid-template-columns: 1fr; }
  .miniStat { min-width: 100%; }
  .torreHeader { flex-direction: column; }
  .torreFiltros { width: 100%; justify-content: stretch; }
  .mesBaseTorre { width: 100%; }
  .mesBaseTorre input { width: 100%; box-sizing: border-box; }
  .periodoTorreTabs { width: 100%; display: grid; grid-template-columns: repeat(2, 1fr); }
  .torreChartArea { overflow-x: auto; }
  .torreChartArea > div { min-width: 760px; }
  .torreLogos { min-width: 760px; grid-template-columns: repeat(10, 70px); overflow: visible; padding-left: 56px; margin-top: -50px; }
  .microGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .rankingItem { grid-template-columns: 32px 42px 1fr; }
  .rankingValor { grid-column: 3; margin-top: -4px; }
}

@media (max-width: 420px) {
  .cardsGrid, .hojeGrid, .mesGrid { grid-template-columns: 1fr; }
  .acoes { grid-template-columns: 1fr; }
  .microGrid { grid-template-columns: 1fr; }
}


/* AJUSTE — números dos cards inferiores sem corte
   Corrige valores como "Meu lucro da rede" ficando cortados no bloco Crescimento da rede. */
.crescimentoRedePainel {
  overflow: visible;
}

.crescimentoRedePainel .crescimentoGrid.interno.rede {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.crescimentoRedePainel .growthCard {
  min-width: 0;
  height: auto;
  min-height: 152px;
  overflow: hidden;
  padding: 12px 10px;
}

.crescimentoRedePainel .growthCard strong {
  display: block;
  width: 100%;
  max-width: 100%;
  font-size: clamp(14px, 0.92vw, 18px);
  line-height: 1.12;
  letter-spacing: -0.35px;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: normal;
}

.crescimentoRedePainel .growthCard p {
  font-size: 10.8px;
  line-height: 1.32;
  margin-top: 7px;
  word-break: normal;
}

.crescimentoRedePainel .growthCard .miniSpark {
  margin-top: auto;
}

@media (max-width: 1400px) {
  .crescimentoRedePainel .crescimentoGrid.interno.rede {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .crescimentoRedePainel .growthCard {
    min-height: 126px;
  }

  .crescimentoRedePainel .growthCard strong {
    font-size: clamp(17px, 1.35vw, 22px);
    white-space: nowrap;
  }
}

@media (max-width: 768px) {
  .crescimentoRedePainel .crescimentoGrid.interno.rede {
    grid-template-columns: 1fr;
  }

  .crescimentoRedePainel .growthCard strong {
    font-size: clamp(20px, 5vw, 27px);
  }
}

`;
